import React, { useEffect, useMemo } from 'react';
import {
  algorithms,
  contentTypes,
  includeModeTypes,
  responseFormats,
} from 'new/components/settings/VendorBuilder/selectTypes';
import {
  FormBuilder,
  FormControlBuilder,
} from 'new/components/settings/VendorBuilder/FormControlBuilder';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { set, unset } from 'lodash';

interface JWTFieldsProps {
  index: number;
}

const jwtFields = (index) => [
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.auth.url"]`,
    label: (
      <>
        JWT: Token URL
        <code>`auth_templates[{index}].config["jwt.auth.url"]</code>
      </>
    ),
    description: 'Enter the token URL that must be called to initiate JWT authentication.',
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.auth.body"]`,
    label: (
      <>
        JWT: Token URL Request Body <code>auth_templates[{index}].config["jwt.auth.body"]</code>
      </>
    ),
    description:
      'Enter the request body for the JWT token URL. This must be entered as a valid JSON object.',
  },
  {
    type: 'select',
    items: contentTypes,
    defaultValue: contentTypes[2].value,
    name: `auth_templates[${index}].config["jwt.auth.content.type"]`,
    label: (
      <>
        JWT: Content Type <code>auth_templates[{index}].config["jwt.auth.content.type"]</code>
      </>
    ),
  },
  {
    type: 'select',
    items: responseFormats,
    defaultValue: responseFormats[1].value,
    name: `auth_templates[${index}].config["jwt.auth.response.format"]`,
    label: (
      <>
        JWT: Token Response Format{' '}
        <code>auth_templates[{index}].config["jwt.auth.response.format"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.auth.token.path"]`,
    label: (
      <>
        JWT: Path to Token <code>auth_templates[{index}].config["jwt.auth.token.path"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.auth.token.type.path"]`,
    label: (
      <>
        JWT: Path to Token Type{' '}
        <code>auth_templates[{index}].config["jwt.auth.token.type.path"]</code>
      </>
    ),
  },
  {
    type: 'select',
    items: includeModeTypes,
    defaultValue: includeModeTypes[0].value,
    name: `auth_templates[${index}].config["jwt.token.include.mode"]`,
    label: (
      <>
        JWT: Token Include Mode{' '}
        <code>auth_templates[{index}].config["jwt.token.include.mode"]</code>
      </>
    ),
  },
  {
    type: 'text',
    dependsOn: {
      [`auth_templates[${index}].config["jwt.token.include.mode"]`]: includeModeTypes[1].value,
    },
    name: `auth_templates[${index}].config["jwt.token.url.parameter"]`,
    label: (
      <>
        JWT: URL Parameter <code>auth_templates[{index}].config["jwt.token.url.parameter"]</code>
      </>
    ),
  },
  {
    type: 'text',
    dependsOn: {
      [`auth_templates[${index}].config["jwt.token.include.mode"]`]: includeModeTypes[0].value,
    },
    name: `auth_templates[${index}].config["jwt.token.header"]`,
    label: (
      <>
        JWT: Token Header <code>auth_templates[{index}].config["jwt.token.header"]</code>
      </>
    ),
  },
  {
    type: 'text',
    dependsOn: {
      [`auth_templates[${index}].config["jwt.token.include.mode"]`]: includeModeTypes[0].value,
    },
    name: `auth_templates[${index}].config["jwt.token.header.type"]`,
    label: (
      <>
        JWT: Token Header Type <code>auth_templates[{index}].config["jwt.token.header.type"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.claim.expiration.sec"]`,
    label: (
      <>
        JWT: Token Expiration Duration(sec){' '}
        <code>auth_templates[{index}].config["jwt.claim.expiration.sec"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.token.secret"]`,
    label: (
      <>
        JWT: Token Secret (optional) <code>auth_templates[{index}].config["jwt.token.secret"]</code>
      </>
    ),
  },
  {
    type: 'select',
    items: algorithms,
    defaultValue: algorithms[1].value,
    name: `auth_templates[${index}].config["jwt.token.algorithm"]`,
    label: (
      <>
        JWT: Token Algorithm <code>auth_templates[{index}].config["jwt.token.algorithm"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.claim.scope"]`,
    label: (
      <>
        JWT: Scope Claim <code>auth_templates[{index}].config["jwt.claim.scope"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.claim.audience"]`,
    label: (
      <>
        JWT: Audience Claim <code>auth_templates[{index}].config["jwt.claim.audience"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.claim.issuer"]`,
    label: (
      <>
        JWT: Issuer Claim <code>auth_templates[{index}].config["jwt.claim.issuer"]</code>
      </>
    ),
  },
  {
    type: 'text',
    name: `auth_templates[${index}].config["jwt.claim.extra.json"]`,
    multiline: true,
    label: (
      <>
        JWT: Extra Claims <code>auth_templates[{index}].config["jwt.claim.extra.json"]</code>
      </>
    ),
  },
];

export const JWTFields = (props: JWTFieldsProps) => {
  const fields = useMemo(() => jwtFields(props.index), [props.index]);
  return <FormBuilder fields={fields} />;
};
